import { builtinPresets } from '../config/presets.js'
import { defaultParams } from '../config/defaults.js'
import { params, saveParams } from '../config/persistence.js'
import { isTransitioning } from '../transition.js'

export const isDebug = new URLSearchParams(window.location.search).get('debug') === 'true'

let activePresetName = null

const detectCurrentPreset = () => {
    const keys = Object.keys(defaultParams)
    for (const [name, preset] of Object.entries(builtinPresets)) {
        const match = keys.every(k => preset[k] === undefined || preset[k] === params[k])
        if (match) return name
    }
    return Object.keys(builtinPresets)[0] || null
}

const positionIndicator = (indicator, pill, bar, animate = true) => {
    const x = pill.offsetLeft
    const w = pill.offsetWidth
    const h = pill.offsetHeight
    if (animate) {
        indicator.style.transition = 'transform 0.55s cubic-bezier(0.16, 1, 0.3, 1), width 0.55s cubic-bezier(0.16, 1, 0.3, 1), height 0.55s cubic-bezier(0.16, 1, 0.3, 1)'
        indicator.style.transform = `translateX(${x}px)`
        indicator.style.width = w + 'px'
        indicator.style.height = h + 'px'
    } else {
        indicator.style.transition = 'none'
        indicator.style.transform = `translateX(${x}px)`
        indicator.style.width = w + 'px'
        indicator.style.height = h + 'px'
    }
}

export const createOverlay = ({ markDirty }) => {
    if (isDebug) return

    // --- Bottom vignette ---
    const vignette = document.createElement('div')
    vignette.className = 'bottom-vignette'
    document.body.appendChild(vignette)

    // --- Container ---
    const container = document.createElement('div')
    container.className = 'bottom-ui'
    document.body.appendChild(container)

    // --- Header row ---
    const header = document.createElement('div')
    header.className = 'bottom-header'
    container.appendChild(header)

    const brandLeft = document.createElement('div')
    brandLeft.className = 'brand-left'
    header.appendChild(brandLeft)

    const topBrand = document.createElement('a')
    topBrand.className = 'brand-author'
    topBrand.href = 'https://niccolofanton.dev'
    topBrand.target = '_blank'
    topBrand.rel = 'noopener'
    topBrand.textContent = 'niccolofanton.dev'
    brandLeft.appendChild(topBrand)

    const brand = document.createElement('span')
    brand.className = 'brand-main'
    brand.textContent = 'porsche shader'
    brandLeft.appendChild(brand)

    // --- Right buttons ---
    const rightButtons = document.createElement('div')
    rightButtons.style.cssText = 'display:flex;gap:4px;align-items:center'
    header.appendChild(rightButtons)

    const rotateToggle = document.createElement('button')
    rotateToggle.className = 'rotate-toggle'
    rotateToggle.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21.5 2v6h-6"/><path d="M21.34 13.05A10 10 0 1 1 18.57 4.43L21.5 2"/></svg>`
    rotateToggle.title = 'Auto-rotate'
    if (params.autoRotate) rotateToggle.classList.add('active')
    rotateToggle.addEventListener('click', () => {
        params.autoRotate = !params.autoRotate
        rotateToggle.classList.toggle('active', params.autoRotate)
        saveParams()
        window.dispatchEvent(new CustomEvent('toggle-rotate', { detail: params.autoRotate }))
    })
    rightButtons.appendChild(rotateToggle)

    // --- Preset bar ---
    const barWrapper = document.createElement('div')
    barWrapper.className = 'preset-bar-wrapper'
    container.appendChild(barWrapper)

    const bar = document.createElement('div')
    bar.className = 'preset-bar'
    barWrapper.appendChild(bar)

    const updateFadeEdges = () => {
        const sl = bar.scrollLeft
        const maxScroll = bar.scrollWidth - bar.clientWidth
        barWrapper.classList.toggle('fade-left', sl > 4)
        barWrapper.classList.toggle('fade-right', sl < maxScroll - 4)
    }
    bar.addEventListener('scroll', updateFadeEdges, { passive: true })

    // Sliding indicator
    const indicator = document.createElement('div')
    indicator.className = 'preset-indicator'
    bar.appendChild(indicator)

    const pills = []
    const currentPresetName = detectCurrentPreset()
    activePresetName = currentPresetName

    Object.entries(builtinPresets).forEach(([name]) => {
        const pill = document.createElement('button')
        pill.className = 'preset-pill'
        pill.textContent = name
        pill.dataset.preset = name
        if (name === currentPresetName) pill.classList.add('active')

        pill.addEventListener('click', () => switchPreset(name, bar, rotateToggle, pills, indicator, markDirty))
        bar.appendChild(pill)
        pills.push(pill)
    })

    // Position indicator on active pill
    requestAnimationFrame(() => {
        updateFadeEdges()
        const activePill = bar.querySelector('.preset-pill.active')
        if (activePill) {
            positionIndicator(indicator, activePill, bar, false)
            const barRect = bar.getBoundingClientRect()
            const pillRect = activePill.getBoundingClientRect()
            const scrollTo = activePill.offsetLeft - (barRect.width / 2) + (pillRect.width / 2)
            bar.scrollLeft = Math.max(0, scrollTo)
            updateFadeEdges()
        }
        // Scroll hint on mobile
        if (bar.scrollWidth > bar.clientWidth) {
            setTimeout(() => { bar.scrollTo({ left: 40, behavior: 'smooth' }) }, 1500)
            setTimeout(() => { bar.scrollTo({ left: 0, behavior: 'smooth' }) }, 2300)
        }
    })
}

const switchPreset = (name, bar, rotateToggle, pills, indicator, markDirty) => {
    const preset = builtinPresets[name]
    if (!preset || isTransitioning() || name === activePresetName) return

    // Update active pill immediately (UI responds instantly)
    pills.forEach(p => {
        p.classList.remove('active')
        p.style.color = ''
    })
    const targetPill = pills.find(p => p.dataset.preset === name)
    if (targetPill) {
        targetPill.classList.add('active')
        positionIndicator(indicator, targetPill, bar, true)

        // Scroll pill into view
        const barRect = bar.getBoundingClientRect()
        const pillRect = targetPill.getBoundingClientRect()
        if (pillRect.left < barRect.left || pillRect.right > barRect.right) {
            const scrollTo = targetPill.offsetLeft - (barRect.width / 2) + (pillRect.width / 2)
            bar.scrollTo({ left: Math.max(0, scrollTo), behavior: 'smooth' })
        }
    }
    activePresetName = name

    // Update rotate toggle to target state immediately
    const targetParams = { ...defaultParams, ...preset }
    if (rotateToggle) rotateToggle.classList.toggle('active', targetParams.autoRotate)

    // Dispatch transition — main.js will morph params over time
    window.dispatchEvent(new CustomEvent('preset-switch', { detail: { target: targetParams } }))
}
